import { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthenticatedApiContext } from '../api/useApiClient';
import { ConfigureIdpRequest, GetIdpsResponse, IdpResponse, GetRolesResponse } from '../models/IdpModels';
import { AsyncState, useAsync } from './useAsync';
import { useAsyncEffect } from './useAsyncEffect';
import { ApiError } from './useApiErrorMsgParser';

export type IdentityProviderAsyncState = AsyncState<IdpResponse[], () => Promise<IdpResponse[]>, Error>;

interface IdpConfigsCtx {
  idpConfigs: IdpResponse[];
  idpRoles: string[];
  loadIdps: IdentityProviderAsyncState;
  createIdp: AsyncState<IdpResponse, (idp: ConfigureIdpRequest) => Promise<IdpResponse>>;
  updateIdp: AsyncState<void, (idp: ConfigureIdpRequest) => Promise<void>>;
  deleteIdp: AsyncState<void, (idpId: string) => Promise<void>>;
}

const IdpConfigsContext = createContext<IdpConfigsCtx>({} as IdpConfigsCtx);

export const IdpConfigsProvider: React.FC = ({ children }) => {
  const idpConfigsHook = useIdpConfigsHook();
  return <IdpConfigsContext.Provider value={{ ...idpConfigsHook }}>{children}</IdpConfigsContext.Provider>;
};

export function useIdentityProviderConfigs(): IdpConfigsCtx {
  return useContext(IdpConfigsContext);
}

const useIdpConfigsHook = (): IdpConfigsCtx => {
  const [idpConfigs, setIdpConfigs] = useState<IdpResponse[]>([]);
  const [idpRoles, setIdpRoles] = useState<string[]>([]);
  const apiClient = useAuthenticatedApiContext();
  const { t } = useTranslation();

  const loadRoles = useAsync(
    useCallback(async (): Promise<GetRolesResponse> => {
      const resp: GetRolesResponse = await apiClient.getRoles();
      setIdpRoles(resp.roles);
      return resp;
    }, [apiClient])
  );
  useAsyncEffect(loadRoles, []);

  const loadIdps = useAsync(
    useCallback(async (): Promise<IdpResponse[]> => {
      const idps: IdpResponse[] = [];
      let nextPageCursor: string | null = null;
      do {
        // eslint-disable-next-line no-await-in-loop
        const resp: GetIdpsResponse = await apiClient.getIdps({
          limit: 30,
          cursor: nextPageCursor,
        });
        idps.push(...resp.data);
        nextPageCursor = resp.nextPageCursor;
      } while (nextPageCursor);

      setIdpConfigs(idps);
      return idps;
    }, [apiClient]),
    { autoReset: true }
  );
  useAsyncEffect(loadIdps, []);

  const createIdp = useAsync(
    useCallback(
      async (idp: ConfigureIdpRequest): Promise<IdpResponse> => {
        const res = await apiClient.createIdp(idp).catch((err: ApiError) => {
          throw new Error(err.displayMessage ?? t('Failed to create IDP'));
        });
        await loadIdps.execute();
        return res;
      },
      [apiClient, loadIdps, t]
    ),
    { autoReset: true }
  );

  const updateIdp = useAsync(
    useCallback(
      async (idp: ConfigureIdpRequest): Promise<void> => {
        await apiClient.updateIdp(idp).catch((err: ApiError) => {
          throw new Error(err.displayMessage ?? t('Failed to update IDP'));
        });
        await loadIdps.execute();
      },
      [apiClient, loadIdps, t]
    ),
    { autoReset: true }
  );

  const deleteIdp = useAsync(
    useCallback(
      async (idpId: string): Promise<void> => {
        await apiClient.deleteIdp(idpId).catch((err: ApiError) => {
          throw new Error(err.displayMessage ?? t('IDP deletion failed'));
        });
        await loadIdps.execute();
      },
      [apiClient, loadIdps, t]
    ),
    { autoReset: true }
  );
  return { idpConfigs, loadIdps, createIdp, updateIdp, deleteIdp, idpRoles };
};
